<template>
  <div v-editable="blok" class="architecture">
    <div v-if="beats.length" ref="statusFrame" class="frame statusframe">
      <div ref="scaled" class="scaled">
        <div class="statusbar">
          <div v-if="blok.status_label" class="status-left">
            <span class="status-dot" />
            <span class="status-label">{{ blok.status_label }}</span>
          </div>
          <div
            class="status-msg"
            :style="{ opacity: messageOpacity }"
            aria-live="polite"
          >
            <span class="lead">{{ lead }}</span>
            <span class="rest">{{ rest }}</span>
          </div>
        </div>
      </div>
    </div>

    <div ref="dframe" class="frame dframe">
      <div ref="diagram" class="diagram">
        <div class="stage">
          <div class="loop">
            <svg class="flowlayer" aria-hidden="true">
              <rect
                class="loop-path"
                width="100%"
                height="100%"
                rx="38"
                fill="none"
                stroke="var(--cobalt-600)"
                stroke-width="1.6"
                stroke-dasharray="6 5"
              />
            </svg>
            <div v-if="blok.loop_label_top" class="loop-pill top">
              {{ blok.loop_label_top }}
            </div>
            <div v-if="blok.loop_label_bottom" class="loop-pill bottom">
              {{ blok.loop_label_bottom }}
            </div>

            <div class="sections">
              <template
                v-for="(category, categoryIndex) in categories"
                :key="category._uid"
              >
                <div v-if="categoryIndex > 0" class="arrow" aria-hidden="true">
                  <svg width="54" height="14" viewBox="0 0 54 14">
                    <line
                      x1="0"
                      y1="7"
                      x2="42"
                      y2="7"
                      stroke="var(--paper-500)"
                      stroke-width="2.6"
                      stroke-linecap="round"
                    />
                    <path
                      d="M41 1.6 L53 7 L41 12.4 Z"
                      fill="var(--paper-500)"
                    />
                    <g
                      class="afill"
                      :class="{ flow: isArrowFlowing(`a${categoryIndex}`) }"
                    >
                      <line
                        class="afill-line"
                        x1="0"
                        y1="7"
                        x2="42"
                        y2="7"
                        pathLength="100"
                        stroke="var(--cobalt-600)"
                        stroke-width="2.6"
                        stroke-linecap="round"
                      />
                      <path
                        class="afill-head"
                        d="M41 1.6 L53 7 L41 12.4 Z"
                        fill="var(--cobalt-600)"
                      />
                    </g>
                  </svg>
                </div>

                <div
                  v-editable="category"
                  class="section"
                  :class="{ active: isCategoryActive(category) }"
                >
                  <svg class="secedge" aria-hidden="true">
                    <rect width="100%" height="100%" rx="16" />
                  </svg>
                  <div v-if="category.name" class="section-label">
                    {{ category.name }}
                  </div>

                  <template
                    v-for="(step, stepIndex) in category.steps"
                    :key="step._uid"
                  >
                    <div
                      v-if="
                        stepIndex > 0 && hasBidirectionalLink(categoryIndex)
                      "
                      class="bilink"
                    >
                      <svg
                        width="22"
                        height="24"
                        viewBox="0 0 22 24"
                        aria-hidden="true"
                      >
                        <g class="bi-up">
                          <path
                            d="M7 21 V5 M4 8 L7 5 L10 8"
                            fill="none"
                            stroke-width="1.7"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </g>
                        <g class="bi-down">
                          <path
                            d="M15 3 V19 M12 16 L15 19 L18 16"
                            fill="none"
                            stroke-width="1.7"
                            stroke-linecap="round"
                            stroke-linejoin="round"
                          />
                        </g>
                      </svg>
                    </div>

                    <component
                      :is="step.scroll_to_element_of_id ? 'button' : 'div'"
                      v-editable="step"
                      :type="
                        step.scroll_to_element_of_id ? 'button' : undefined
                      "
                      class="card"
                      :class="{
                        active: isStepActive(step),
                        'is-clickable': !!step.scroll_to_element_of_id,
                      }"
                      @click="
                        scrollToElement(step.scroll_to_element_of_id || '')
                      "
                    >
                      <div class="card-head">
                        <span
                          class="dot"
                          :style="{ background: step.dot_color }"
                        />
                        <span class="card-title">{{ step.header }}</span>
                      </div>
                      <div class="card-body">{{ step.body }}</div>
                    </component>
                  </template>
                </div>
              </template>
            </div>
          </div>

          <div v-if="outputs.length" class="out-arrow" aria-hidden="true">
            <svg width="62" height="14" viewBox="0 0 62 14">
              <line
                x1="0"
                y1="7"
                x2="50"
                y2="7"
                stroke="var(--paper-500)"
                stroke-width="2.6"
                stroke-linecap="round"
              />
              <path d="M49 1.6 L61 7 L49 12.4 Z" fill="var(--paper-500)" />
              <g
                class="afill"
                :class="{ flow: isArrowFlowing(outputArrowName) }"
              >
                <line
                  class="afill-line"
                  x1="0"
                  y1="7"
                  x2="50"
                  y2="7"
                  pathLength="100"
                  stroke="var(--cobalt-600)"
                  stroke-width="2.6"
                  stroke-linecap="round"
                />
                <path
                  class="afill-head"
                  d="M49 1.6 L61 7 L49 12.4 Z"
                  fill="var(--cobalt-600)"
                />
              </g>
            </svg>
          </div>

          <div
            v-if="outputContainer && outputs.length"
            v-editable="outputContainer"
            class="output"
            :class="{
              'is-clickable': !!outputContainer.scroll_to_element_of_id,
            }"
            @click="
              scrollToElement(outputContainer.scroll_to_element_of_id || '')
            "
          >
            <component
              :is="
                item.scroll_to_element_of_id ||
                outputContainer.scroll_to_element_of_id
                  ? 'button'
                  : 'div'
              "
              v-for="item in outputs"
              :key="item._uid"
              v-editable="item"
              :type="
                item.scroll_to_element_of_id ||
                outputContainer.scroll_to_element_of_id
                  ? 'button'
                  : undefined
              "
              class="outbox"
              :class="{
                active: outputActive,
                'is-clickable': !!(
                  item.scroll_to_element_of_id ||
                  outputContainer.scroll_to_element_of_id
                ),
              }"
              @click.stop="
                scrollToElement(
                  item.scroll_to_element_of_id ||
                    outputContainer.scroll_to_element_of_id ||
                    '',
                )
              "
            >
              {{ item.text }}
            </component>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import type {
  SbSystemArchitectureCategoryStoryblok,
  SbSystemArchitectureStepStoryblok,
  SbSystemArchitectureStoryblok,
} from '@/types'

interface StatusBeat {
  lead: string
  text: string
  stepUid?: string
  arrow?: string
  highlightOutput?: boolean
}

interface Props {
  blok: SbSystemArchitectureStoryblok
}

const props = defineProps<Props>()
const { scrollToElement } = useScrollToElement()

const categories = computed(() => props.blok.categories ?? [])
const outputContainer = computed(() => props.blok.output?.[0])
const outputs = computed(() => outputContainer.value?.outputs ?? [])
const outputArrowName = computed(() => `a${categories.value.length}`)

const beats = computed<StatusBeat[]>(() => {
  const timeline: StatusBeat[] = []

  if (props.blok.first_status_lead || props.blok.first_status_text) {
    timeline.push({
      lead: props.blok.first_status_lead ?? '',
      text: props.blok.first_status_text ?? '',
    })
  }

  categories.value.forEach((category, categoryIndex) => {
    category.steps?.forEach((step, stepIndex) => {
      timeline.push({
        lead: step.status_lead ?? '',
        text: step.status_text ?? '',
        stepUid: step._uid,
        arrow:
          categoryIndex > 0 && stepIndex === 0
            ? `a${categoryIndex}`
            : undefined,
      })
    })
  })

  const container = outputContainer.value
  if (container && (container.status_lead || container.status_text)) {
    timeline.push({
      lead: container.status_lead ?? '',
      text: container.status_text ?? '',
      highlightOutput: true,
      arrow: categories.value.length ? outputArrowName.value : undefined,
    })
  }

  return timeline
})

const stepInterval = computed(() => {
  const value = Number(props.blok.step_interval)
  return Number.isFinite(value) && value > 0 ? value : 3600
})

const lead = ref('')
const rest = ref('')
const messageOpacity = ref(1)
const beatIndex = ref(0)
const activeStepUid = ref('')
const outputActive = ref(false)
const flowingArrows = ref<string[]>([])

const statusFrame = ref<HTMLElement | null>(null)
const scaled = ref<HTMLElement | null>(null)
const dframe = ref<HTMLElement | null>(null)
const diagram = ref<HTMLElement | null>(null)

const DW = 1386
const DH = 567
const SBH = 64
const CAP = 998
const BP = 900

const isStepActive = (step: SbSystemArchitectureStepStoryblok) =>
  step._uid === activeStepUid.value

const isCategoryActive = (category: SbSystemArchitectureCategoryStoryblok) =>
  category.steps?.some(isStepActive) ?? false

const hasBidirectionalLink = (categoryIndex: number) => {
  const category = categories.value[categoryIndex]
  return (
    categoryIndex < categories.value.length - 1 &&
    (category?.steps?.length ?? 0) > 1
  )
}

const isArrowFlowing = (name: string) => flowingArrows.value.includes(name)

let stepTimer: ReturnType<typeof setInterval> | undefined
let fadeTimer: ReturnType<typeof setTimeout> | undefined
let resizeObserver: ResizeObserver | undefined

const applyBeat = (index: number, animateText = true) => {
  const beat = beats.value[index]
  if (!beat) return

  beatIndex.value = index

  const showText = () => {
    lead.value = beat.lead
    rest.value = beat.text ? ` ${beat.text}` : ''
    messageOpacity.value = 1
  }

  if (animateText) {
    messageOpacity.value = 0
    clearTimeout(fadeTimer)
    fadeTimer = setTimeout(showText, 170)
  } else {
    clearTimeout(fadeTimer)
    showText()
  }

  if (index === 0) {
    flowingArrows.value = beat.arrow ? [beat.arrow] : []
  } else if (beat.arrow && !flowingArrows.value.includes(beat.arrow)) {
    flowingArrows.value = [...flowingArrows.value, beat.arrow]
  }

  activeStepUid.value = beat.stepUid ?? ''
  outputActive.value = !!beat.highlightOutput
}

const fit = () => {
  const wide = document.documentElement.clientWidth > BP
  const rendered = Math.min(CAP, document.documentElement.clientWidth)
  const scale = rendered / DW

  if (diagram.value && dframe.value) {
    if (wide) {
      diagram.value.style.transform = `scale(${scale})`
      dframe.value.style.width = `${rendered}px`
      const contentHeight = Math.max(diagram.value.offsetHeight, DH)
      dframe.value.style.height = `${contentHeight * scale}px`
    } else {
      diagram.value.style.transform = ''
      dframe.value.style.width = ''
      dframe.value.style.height = ''
    }
  }

  if (scaled.value && statusFrame.value) {
    if (wide) {
      scaled.value.style.transform = `scale(${scale})`
      statusFrame.value.style.width = `${rendered}px`
      statusFrame.value.style.height = `${SBH * scale}px`
    } else {
      scaled.value.style.transform = ''
      statusFrame.value.style.width = ''
      statusFrame.value.style.height = ''
    }
  }
}

watch(beats, (next) => {
  if (!next.length) return
  const index = Math.min(beatIndex.value, next.length - 1)
  applyBeat(index, false)
})

onMounted(() => {
  applyBeat(0, false)
  fit()
  window.addEventListener('resize', fit)
  document.fonts?.ready.then(() => fit())

  if (diagram.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => fit())
    resizeObserver.observe(diagram.value)
  }

  if (beats.value.length > 1) {
    stepTimer = setInterval(() => {
      const next = (beatIndex.value + 1) % beats.value.length
      applyBeat(next)
    }, stepInterval.value)
  }
})

onUnmounted(() => {
  clearInterval(stepTimer)
  clearTimeout(fadeTimer)
  resizeObserver?.disconnect()
  window.removeEventListener('resize', fit)
})
</script>

<style lang="scss" scoped>
.architecture {
  --status: #12b886;
  --status-label: #0fa877;

  background: var(--paper-100);
  color: var(--cobalt-800);
  font-family: var(--font-family);
  font-size: 16px;
  font-weight: var(--font-weight-normal);
  line-height: normal;
  letter-spacing: normal;
  padding: 22px 0 24px;
  border-radius: 20px;
}

.frame {
  width: 998px;
  max-width: 100%;
  margin: 0 auto;
  overflow: hidden;
}

.statusframe {
  height: 47px;
  margin: 0 auto 12px;
}

.dframe {
  height: 408px;
  margin: 0 auto;
}

.scaled {
  width: 1386px;
  transform: scale(0.72006);
  transform-origin: top left;
}

.diagram {
  width: 1386px;
  min-height: 567px;
  height: auto;
  background: var(--paper-100);
  transform: scale(0.72006);
  transform-origin: top left;
}

.stage {
  display: flex;
  align-items: stretch;
  min-height: 567px;
  height: auto;
  padding: 26px 24px 0 24px;
}

.flowlayer {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;
}

.loop-path {
  animation: loopflow 1.6s linear infinite;
}

@keyframes loopflow {
  to {
    stroke-dashoffset: -22;
  }
}

.statusbar {
  width: 1338px;
  height: 64px;
  margin: 0 24px;
  background: var(--white);
  border-radius: 18px;
  box-shadow: 0 8px 26px rgba(0, 0, 51, 0.06);
  padding: 0 30px;
  display: flex;
  align-items: center;
  gap: 30px;
}

.status-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 0 0 246px;
}

.status-dot {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  background: var(--status);
  position: relative;
  flex: 0 0 14px;

  &::after {
    content: '';
    position: absolute;
    inset: -5px;
    border-radius: 50%;
    border: 2px solid var(--status);
    opacity: 0.5;
    animation: pulse 1.8s ease-out infinite;
  }
}

@keyframes pulse {
  0% {
    transform: scale(0.7);
    opacity: 0.6;
  }

  100% {
    transform: scale(1.5);
    opacity: 0;
  }
}

.status-label {
  color: var(--status-label);
  font-size: 15px;
  font-weight: var(--font-weight-bold);
  letter-spacing: 1.1px;
  white-space: nowrap;
}

.status-msg {
  font-size: 16px;
  color: var(--cobalt-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: opacity 0.18s ease;

  .lead {
    font-weight: var(--font-weight-bold);
  }

  .rest {
    font-weight: var(--font-weight-normal);
  }
}

.loop {
  position: relative;
  flex: 1;
  border: none;
  border-radius: 22px;
  padding: 44px 26px;
  margin: 22px 0;
}

.loop-pill {
  position: absolute;
  left: 50%;
  transform: translateX(-50%);
  background: var(--white);
  border: 1.6px solid var(--cobalt-600);
  color: var(--cobalt-600);
  font-size: 15px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 1.1px;
  padding: 9px 24px;
  border-radius: 20px;
  white-space: nowrap;

  &.top {
    top: -20px;
  }

  &.bottom {
    bottom: -20px;
  }
}

.sections {
  display: flex;
  align-items: stretch;
  height: 100%;
}

.section {
  position: relative;
  flex: 1;
  border: none;
  border-radius: 16px;
  padding: 32px 18px 22px;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 20px;
}

.secedge {
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  overflow: visible;
  pointer-events: none;

  rect {
    fill: none;
    stroke: var(--paper-500);
    stroke-width: 1.6;
    stroke-dasharray: 6 5;
    transition: stroke 0.45s ease;
    animation: loopflow 1.6s linear infinite;
  }
}

.section.active .secedge rect {
  stroke: var(--cobalt-600);
}

.section-label {
  position: absolute;
  top: -17px;
  left: 50%;
  transform: translateX(-50%);
  background: var(--paper-600);
  color: var(--white);
  font-size: 15px;
  font-weight: var(--font-weight-semibold);
  letter-spacing: 1px;
  padding: 8px 18px;
  border-radius: 17px;
  white-space: nowrap;
  transition: background-color 0.45s ease;
}

.section.active > .section-label {
  background: var(--cobalt-600);
}

.arrow {
  flex: 0 0 54px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.out-arrow {
  flex: 0 0 62px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.arrow svg,
.out-arrow svg {
  display: block;
  overflow: visible;
}

.afill {
  opacity: 0;
  transition: opacity 0.15s ease;
}

.afill-line {
  stroke-dasharray: 100;
  stroke-dashoffset: 100;
  transition: stroke-dashoffset 0.7s linear;
}

.afill-head {
  opacity: 0;
  transition: opacity 0.2s linear 0.5s;
}

.afill.flow {
  opacity: 1;

  .afill-line {
    stroke-dashoffset: 0;
  }

  .afill-head {
    opacity: 1;
  }
}

.card,
.outbox {
  background: var(--white);
  border: 1px solid var(--paper-200);
  text-align: left;
  color: inherit;
  font: inherit;
}

.card {
  display: block;
  width: 100%;
  border-radius: 14px;
  padding: 16px 18px;
  box-shadow: 0 4px 12px rgba(0, 0, 51, 0.045);
  transition: border-color 0.45s ease;
}

.outbox {
  border-radius: 12px;
  padding: 13px 16px;
  font-size: 14.5px;
  font-weight: var(--font-weight-normal);
  line-height: 1.3;
  transition: border-color 0.45s ease;
}

.is-clickable {
  cursor: pointer;

  &:hover {
    border-color: var(--cobalt-600);
  }

  &:focus-visible {
    outline: 2px solid var(--cobalt-600);
    outline-offset: 3px;
  }
}

.card.active,
.card.active.is-clickable:hover,
.outbox.active,
.outbox.active.is-clickable:hover {
  border-color: var(--cobalt-600);
}

.card-head {
  display: flex;
  align-items: center;
  gap: 11px;
  margin-bottom: 8px;
}

.dot {
  width: 13px;
  height: 13px;
  border-radius: 50%;
  flex: 0 0 13px;
  opacity: 0.5;
  transition: opacity 0.45s ease;
}

.card.active .dot {
  opacity: 1;
}

.card-title {
  font-size: 17px;
  font-weight: var(--font-weight-semibold);
  color: var(--cobalt-800);
  line-height: 1.2;
  white-space: nowrap;
}

.card-body {
  font-size: 15px;
  font-weight: var(--font-weight-normal);
  line-height: 1.45;
  color: var(--cobalt-800);
}

.bilink {
  display: flex;
  justify-content: center;
  align-items: center;
  margin: -4px 0;

  path {
    stroke: var(--paper-500);
    transition: stroke 0.45s ease;
  }
}

.section.active .bilink path {
  stroke: var(--cobalt-600);
}

.section.active .bi-up {
  animation: biup 1.15s ease-in-out infinite;
}

.section.active .bi-down {
  animation: bidown 1.15s ease-in-out infinite;
}

@keyframes biup {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(-4px);
  }
}

@keyframes bidown {
  0%,
  100% {
    transform: translateY(0);
  }

  50% {
    transform: translateY(4px);
  }
}

.output {
  flex: 0 0 180px;
  align-self: center;
  display: flex;
  flex-direction: column;
  gap: 11px;
}

@media (prefers-reduced-motion: reduce) {
  .loop-path,
  .secedge rect,
  .bi-up,
  .bi-down,
  .status-dot::after {
    animation: none;
  }
}

@media (max-width: 511px) {
  .frame {
    width: 100%;
    overflow: visible;
  }

  .statusframe,
  .dframe {
    height: auto;
  }

  .scaled {
    width: 100%;
    transform: none;
  }

  .diagram {
    width: 100%;
    height: auto;
    min-height: 0;
    transform: none;
  }

  .architecture {
    padding: 14px 0 20px;
  }

  .statusframe {
    margin: 0 auto 10px;
  }

  .statusbar {
    width: auto;
    height: auto;
    margin: 0 12px;
    padding: 14px 18px;
    border-radius: 14px;
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .status-left {
    flex: none;
    gap: 10px;
  }

  .status-label {
    font-size: 12px;
    letter-spacing: 1px;
  }

  .status-msg {
    white-space: normal;
    overflow: visible;
    font-size: 14px;
    line-height: 1.4;
  }

  .stage {
    flex-direction: column;
    height: auto;
    min-height: 0;
    padding: 14px 12px 0;
  }

  .loop {
    flex: none;
    margin: 24px 0;
    padding: 34px 14px;
  }

  .sections {
    flex-direction: column;
    height: auto;
    gap: 0;
    margin-top: 12px;
  }

  .section {
    flex: none;
    padding: 30px 16px 20px;
  }

  .card-title {
    white-space: normal;
  }

  .arrow,
  .out-arrow {
    flex: none;
    width: 100%;
    height: 40px;
    margin: 12px 0;
  }

  .arrow svg,
  .out-arrow svg {
    transform: rotate(90deg);
    width: 40px;
    height: auto;
  }

  .out-arrow svg {
    width: 34px;
  }

  .output {
    flex: none;
    width: 100%;
    flex-direction: row;
    flex-wrap: wrap;
    gap: 10px;
  }

  .outbox {
    flex: 1 1 calc(50% - 6px);
    font-size: 14px;
  }
}

@media (max-width: 350px) {
  .architecture {
    padding-top: 12px;
  }

  .statusframe {
    margin: 0 auto 8px;
  }

  .statusbar {
    margin: 0 10px;
    padding: 12px 14px;
    gap: 6px;
  }

  .status-label {
    font-size: 11px;
    letter-spacing: 0.8px;
  }

  .status-msg {
    font-size: 13px;
  }

  .stage {
    padding: 10px 8px 0;
  }

  .loop {
    margin: 22px 0;
    padding: 30px 10px;
  }

  .section {
    padding: 26px 11px 16px;
  }

  .section-label {
    font-size: 11px;
    letter-spacing: 0.6px;
    padding: 7px 13px;
  }

  .loop-pill {
    font-size: 11px;
    letter-spacing: 0.8px;
    padding: 8px 18px;
  }

  .card {
    padding: 14px;
  }

  .card-title {
    font-size: 15.5px;
  }

  .card-body {
    font-size: 14px;
  }

  .arrow,
  .out-arrow {
    height: 36px;
    margin: 12px 0;
  }

  .arrow svg,
  .out-arrow svg {
    width: 36px;
  }

  .out-arrow svg {
    width: 30px;
  }

  .output {
    flex-direction: column;
    flex-wrap: nowrap;
  }

  .outbox {
    flex: 1 1 auto;
    font-size: 13px;
    padding: 12px 14px;
  }
}

@media (min-width: 512px) and (max-width: 900px) {
  .frame {
    width: 100%;
    overflow: visible;
  }

  .statusframe,
  .dframe {
    height: auto;
  }

  .scaled {
    width: 100%;
    transform: none;
  }

  .diagram {
    width: 100%;
    height: auto;
    min-height: 0;
    transform: none;
  }

  .architecture {
    padding: 16px 0 22px;
  }

  .statusframe {
    margin: 0 auto 10px;
  }

  .statusbar {
    width: auto;
    height: auto;
    margin: 0 18px;
    padding: 14px 22px;
    gap: 20px;
    border-radius: 16px;
  }

  .status-left {
    flex: 0 0 auto;
  }

  .status-label {
    font-size: 11px;
    letter-spacing: 1px;
  }

  .status-msg {
    white-space: normal;
    overflow: visible;
    font-size: 12px;
    line-height: 1.4;
  }

  .stage {
    flex-direction: column;
    height: auto;
    min-height: 0;
    padding: 16px 16px 0;
  }

  .loop {
    flex: none;
    margin: 24px 0;
    padding: 34px 14px;
  }

  .sections {
    flex-direction: row;
    height: auto;
    gap: 0;
  }

  .section {
    flex: 1;
    min-width: 0;
    padding: 28px 11px 16px;
  }

  .card {
    padding: 13px;
  }

  .card-title {
    white-space: normal;
    font-size: 12px;
    line-height: 1.25;
  }

  .card-body {
    font-size: 11px;
    line-height: 1.45;
  }

  .section-label {
    font-size: 11px;
    letter-spacing: 0.5px;
    padding: 6px 11px;
  }

  .loop-pill {
    font-size: 11px;
    letter-spacing: 0.8px;
    padding: 7px 16px;
  }

  .dot {
    width: 11px;
    height: 11px;
    flex: 0 0 11px;
  }

  .arrow {
    flex: 0 0 24px;
    height: auto;
  }

  .arrow svg {
    transform: none;
    width: 24px;
  }

  .out-arrow {
    flex: none;
    width: 100%;
    height: 28px;
    margin: 12px 0;
  }

  .out-arrow svg {
    transform: rotate(90deg);
    width: 28px;
    height: auto;
  }

  .output {
    flex: none;
    width: 100%;
    flex-direction: row;
    flex-wrap: nowrap;
    gap: 9px;
  }

  .outbox {
    flex: 1 1 0;
    min-width: 0;
    font-size: 11.5px;
    padding: 11px;
  }
}
</style>
